import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { DocumentSearch, DocumentSearchStatus, RockManualsJob, RockManualsJobStatus } from '@rock/types';
// isHttpUrl is the SAME predicate the worker uses to accept candidate links — a link rejected here would
// otherwise silently vanish from the search on the worker side.
import { isHttpUrl } from '@rock/util';
import { Ban, Link2, RefreshCw, Upload } from 'lucide-react';
import { type FC, type ReactNode, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { describeUrl } from './documentShared';

const SEARCH_STATUS_META: Record<DocumentSearchStatus, { label: string; className: string }> = {
    requested: { label: 'Queued', className: 'bg-sky-100 text-sky-900' },
    running: { label: 'Searching…', className: 'bg-sky-100 text-sky-900' },
    succeeded: { label: 'Found a candidate', className: 'bg-emerald-100 text-emerald-900' },
    'gave-up': { label: 'Gave up', className: 'bg-rose-100 text-rose-900' },
};

function jobBadgeVariant(status: RockManualsJobStatus): 'success' | 'destructive' | 'secondary' {
    if (status === 'succeeded' || status === 'reviewed') return 'success';
    if (status === 'failed') return 'destructive';
    return 'secondary';
}

// `unavailable` is a legitimate terminal outcome (worker is confident no public manual exists) — distinct
// from `failed` (an error exhausted retries). Amber flags it for a look without reading as broken/red.
// `cancelled` (admin stopped the hunt) gets a muted slate so it reads as inactive, not as pending.
function jobBadgeClassName(status: RockManualsJobStatus): string | undefined {
    if (status === 'unavailable') return 'border-transparent bg-amber-100 text-amber-900';
    if (status === 'cancelled') return 'border-transparent bg-slate-200 text-slate-700';
    return undefined;
}

// The two non-terminal states — the only ones where cancelling future attempts means anything.
function jobIsActive(status: RockManualsJobStatus): boolean {
    return status === 'pending' || status === 'running';
}

export interface AcquisitionCardProps {
    searches: DocumentSearch[];
    job: RockManualsJob | undefined;
    saving: boolean;
    /** A file offer is uploading (its own spinner — the upload happens before the search is created). */
    isUploading: boolean;
    uploadProgress: number;
    /** Create a search request: a plain re-run, or a BYOM offer when `offeredUrl` is set. */
    onRequestSearch: (offeredUrl?: string) => Promise<boolean>;
    /** Offer a PDF you already have: it's staged, then verified like any other offer. */
    onOfferFile: (file: File) => Promise<boolean>;
    /** Cancel the worker job's FUTURE attempts (an in-flight attempt is left to finish). */
    onCancelJob: () => Promise<boolean>;
}

/**
 * The acquisition side: search requests + the worker queue job. A plain "Re-run search" carries NO
 * dispute — it means "run again with current information" (typically after adding a Known As identity);
 * steering away from a wrong document is the candidate's Not Relevant action. Both offers pin a specific
 * document ahead of any discovery spend: a URL for Rock to fetch, or a file you already have. Neither
 * skips verification, and neither publishes anything — offers land under Candidates like everything else.
 */
export const AcquisitionCard: FC<AcquisitionCardProps> = ({
    searches,
    job,
    saving,
    isUploading,
    uploadProgress,
    onRequestSearch,
    onOfferFile,
    onCancelJob,
}) => {
    const [offerOpen, setOfferOpen] = useState(false);
    const [offerFileOpen, setOfferFileOpen] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const latest = searches[0];
    // A cancelled job has no active hunt, so a lingering requested/running search must NOT keep Offer/Re-run
    // disabled — handing the model a file/URL is the whole point of cancelling. This holds regardless of what
    // the worker does with the search doc afterward (or whether it's even on the cancel-aware build yet).
    const searchInFlight =
        (latest?.status === 'requested' || latest?.status === 'running') && job?.status !== 'cancelled';
    // Once the worker job is done, past search attempts are noise — the job badge already says "done".
    const jobDone = job?.status === 'succeeded' || job?.status === 'reviewed';
    const showHistory = historyOpen || searchInFlight || !jobDone;

    return (
        <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
                <div className="space-y-1.5">
                    <CardTitle>Acquisition</CardTitle>
                    <CardDescription>
                        Rock hunts the user manual on its own; every PDF it fetches lands under Candidates for review.
                        Re-run after adding identities (Known As) — it re-searches with the richer claim. Already have
                        the document? Offer it as a link or a file and Rock verifies that one first.
                    </CardDescription>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setOfferOpen(true)}
                        disabled={saving || isUploading || searchInFlight}
                    >
                        <Link2 className="h-4 w-4" />
                        Offer URL…
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setOfferFileOpen(true)}
                        disabled={saving || isUploading || searchInFlight}
                    >
                        <Upload className="h-4 w-4" />
                        {isUploading ? `Uploading… ${Math.round(uploadProgress)}%` : 'Offer file…'}
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => void onRequestSearch()}
                        disabled={saving || isUploading || searchInFlight}
                    >
                        <RefreshCw className="h-4 w-4" />
                        {searchInFlight ? 'Search queued…' : 'Re-run search'}
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="space-y-3">
                {latest ? (
                    showHistory ? (
                        <div className="space-y-1">
                            {searches.slice(0, 3).map((search) => (
                                <SearchRow key={search.id} search={search} />
                            ))}
                        </div>
                    ) : (
                        <Button
                            type="button"
                            variant="link"
                            size="sm"
                            className="h-auto p-0 text-xs"
                            onClick={() => setHistoryOpen(true)}
                        >
                            Show search history ({searches.length})
                        </Button>
                    )
                ) : (
                    <div className="section-description">No searches requested yet.</div>
                )}

                {job && (
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <span className="font-medium">Worker job</span>
                        <Badge
                            variant={jobBadgeVariant(job.status)}
                            className={cn('capitalize', jobBadgeClassName(job.status))}
                        >
                            {job.status}
                        </Badge>
                        <span>
                            {job.attempts} attempt{job.attempts === 1 ? '' : 's'} · updated{' '}
                            {new Date(job.updatedAt).toLocaleString()}
                        </span>
                        {job.lastError && job.status !== 'succeeded' && <span>· {job.lastError}</span>}
                        {/* Cancel only offers itself while there's future work to stop; it leaves an in-flight
                            attempt running (the worker never re-reads the job mid-attempt) and just prevents
                            the next one. Re-run search / an offer revives it. */}
                        {jobIsActive(job.status) && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="ml-auto h-6 gap-1 px-2 text-destructive hover:text-destructive"
                                onClick={() => void onCancelJob()}
                                disabled={saving}
                            >
                                <Ban className="h-3.5 w-3.5" />
                                Cancel job
                            </Button>
                        )}
                    </div>
                )}
            </CardContent>

            <OfferUrlDialog
                open={offerOpen}
                onOpenChange={setOfferOpen}
                saving={saving}
                onSubmit={(url) => onRequestSearch(url)}
            />
            <OfferFileDialog
                open={offerFileOpen}
                onOpenChange={setOfferFileOpen}
                isUploading={isUploading}
                onSubmit={onOfferFile}
            />
        </Card>
    );
};

const SearchRow: FC<{ search: DocumentSearch }> = ({ search }) => {
    const meta = SEARCH_STATUS_META[search.status];
    // `describeUrl` labels a file offer "Uploaded file" rather than showing the raw Storage URL; the link
    // still works — an offer is parked under `offers/`, which nothing overwrites.
    const offer = describeUrl(search.offeredUrl);
    return (
        <div className="flex flex-wrap items-baseline gap-x-2 text-xs">
            <Badge variant="secondary" className={`border-transparent ${meta.className}`}>
                {meta.label}
            </Badge>
            <span className="text-muted-foreground">requested {new Date(search.requestedAt).toLocaleString()}</span>
            {/* A system-filed request (a revisit signal), so the history never reads as "an admin clicked". */}
            {search.origin === 'revisit' && (
                <Badge variant="outline" className="text-violet-900">
                    auto · revisit
                </Badge>
            )}
            {offer && search.offeredUrl && <OfferLink url={search.offeredUrl}>{offer.label}</OfferLink>}
            {/* The offer's OWN yield, separate from the search status — a search can succeed off memory
                or discovery after the offered URL produced nothing, and the green badge alone would read
                as "the offer worked". */}
            {search.offerOutcome === 'candidate' && (
                <span className="text-emerald-600">
                    produced a candidate{search.offerNote ? ` — ${search.offerNote}` : ''}
                </span>
            )}
            {search.offerOutcome === 'none' && (
                <span className="text-amber-700">
                    yielded nothing new{search.offerNote ? ` — ${search.offerNote}` : ''}
                </span>
            )}
            {search.lastError && search.status !== 'succeeded' && (
                <span className="text-muted-foreground">{search.lastError}</span>
            )}
        </div>
    );
};

const OfferLink: FC<{ url: string; children: ReactNode }> = ({ url, children }) => (
    <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="break-all text-foreground underline-offset-4 hover:underline"
    >
        {children}
    </a>
);

const OfferUrlDialog: FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    saving: boolean;
    onSubmit: (url: string) => Promise<boolean>;
}> = ({ open, onOpenChange, saving, onSubmit }) => {
    const [url, setUrl] = useState('');
    // Clear the field each time the dialog opens.
    useEffect(() => {
        if (open) setUrl('');
    }, [open]);

    const submit = async () => {
        const trimmed = url.trim();
        if (!trimmed || !isHttpUrl(trimmed)) {
            toast.error('Enter a full http(s) link to the document');
            return;
        }
        if (await onSubmit(trimmed)) onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Offer a document URL</DialogTitle>
                    <DialogDescription>
                        Rock fetches, verifies, and stages the link in the background — it lands under Candidates for
                        you to review and publish. Nothing reaches the app until you publish it.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-2">
                    <Label htmlFor="offer-url">Document URL</Label>
                    <Input
                        id="offer-url"
                        placeholder="https://example.com/manual.pdf"
                        value={url}
                        onChange={(event) => setUrl(event.target.value)}
                        disabled={saving}
                        inputMode="url"
                        autoComplete="off"
                    />
                    <div className="field-hint">A direct PDF link, or a page that links to it.</div>
                </div>
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                        Cancel
                    </Button>
                    <Button type="button" onClick={() => void submit()} disabled={saving}>
                        Offer URL
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

/**
 * Offering a file is the URL offer minus the fetch: the PDF is staged where Rock stages everything it
 * finds, and the search points at it. Same verification, same Candidates review, same publish step —
 * uploading is how the document ARRIVES, not a way around the pipeline.
 */
const OfferFileDialog: FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    isUploading: boolean;
    onSubmit: (file: File) => Promise<boolean>;
}> = ({ open, onOpenChange, isUploading, onSubmit }) => {
    const [file, setFile] = useState<File | null>(null);
    // Clear the picker each time the dialog opens.
    useEffect(() => {
        if (open) setFile(null);
    }, [open]);

    const submit = async () => {
        if (!file) return;
        if (await onSubmit(file)) onOpenChange(false);
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (isUploading) return;
                onOpenChange(next);
            }}
        >
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Offer a document file</DialogTitle>
                    <DialogDescription>
                        Same as offering a URL, for a PDF you already have. Rock stages it, verifies it, and analyzes
                        its languages — it lands under Candidates for you to review and publish. Nothing reaches the app
                        until you publish it.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-2">
                    <Label htmlFor="offer-file">PDF</Label>
                    <Input
                        id="offer-file"
                        type="file"
                        accept=".pdf,application/pdf"
                        onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                        disabled={isUploading}
                        className="h-8"
                    />
                    <div className="field-hint">
                        Tags, title, and coverage are chosen when you publish the candidate — the verifier's read of the
                        document suggests them.
                    </div>
                </div>
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isUploading}>
                        Cancel
                    </Button>
                    <Button type="button" onClick={() => void submit()} disabled={isUploading || !file}>
                        {isUploading ? 'Uploading…' : 'Offer file'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
